import { useState, useEffect } from "react";
import api, { formatApiError, maybeFunnelPrompt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";
import { CalendarPlus, UserPlus, Loader2, Check, ChevronsUpDown, Sparkles } from "lucide-react";
import FlagPicker, { SETTORI, FONTI } from "@/components/SettoriPicker";

const ACT_TYPES = [["telefonata", "Telefonata"], ["videocall", "Video call"], ["email_contatto", "Email / contatto"]];
const todayISO = () => new Date().toISOString().slice(0, 10);
const EMPTY_PR = { name: "", region: "", province: "", city: "", address: "", cap: "", partita_iva: "", referente: "", email: "", phone: "", next_action: "", notes: "", categoria: "", tipo_cliente: "azienda", settore: "", fonte_contatto: "", note_inseritore: "" };

function TargetCombobox({ value, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);

  // Ricerca lato server (typeahead con debounce): l'elenco completo è troppo grande per il client.
  useEffect(() => {
    const s = query.trim();
    if (s.length < 2) { setList([]); setLoading(false); return; }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const r = await api.get(`/anagrafiche/search?q=${encodeURIComponent(s)}&limit=25`);
        setList(r.data.items || []);
      } catch { setList([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const handleSelect = (p) => { setPicked(p); onSelect(p); setOpen(false); };
  const label = picked && picked.id === value ? picked.name : (value ? "Selezionato" : "Seleziona cliente o prospect…");
  const q = query.trim();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="quickadd-partner-combobox">
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Cerca cliente o prospect… (min 2 lettere)" value={query} onValueChange={setQuery} data-testid="quickadd-partner-search" />
          <CommandList>
            <CommandEmpty>{loading ? "Ricerca…" : q.length < 2 ? "Digita almeno 2 lettere" : "Nessun risultato"}</CommandEmpty>
            <CommandGroup>
              {list.map((p) => (
                <CommandItem key={`${p.kind}-${p.id}`} value={`${p.kind}-${p.id}`} onSelect={() => handleSelect(p)} data-testid={`quickadd-partner-opt-${p.id}`}>
                  <Check className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{p.name}</span>
                  <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${p.kind === "prospect" ? "bg-sky-500/15 text-sky-400" : "bg-emerald-500/15 text-emerald-400"}`}>{p.kind === "prospect" ? "Prospect" : "Cliente"}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function QuickAdd({ partnerId = null, partnerName = "", agencyId = null, defaultDate = null, onChanged, show = ["activity", "prospect"], regions = [], size = "sm", variant = "outline", activityOpen, onActivityOpenChange, hideTriggers = false, prospectId = null, prospectName = "" }) {
  const [actOpenInt, setActOpenInt] = useState(false);
  const actOpen = activityOpen !== undefined ? activityOpen : actOpenInt;
  const setActOpen = (v) => { onActivityOpenChange ? onActivityOpenChange(v) : setActOpenInt(v); };
  const [prOpen, setPrOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checkingNote, setCheckingNote] = useState(false);
  const [noteFeedback, setNoteFeedback] = useState(null);
  const [dmAi, setDmAi] = useState(false);

  const checkNote = async (text) => {
    const t = (text ?? act.notes ?? "").trim();
    if (t.length < 10) { setNoteFeedback(null); return; }
    setCheckingNote(true);
    try { const r = await api.post("/activities/check-note", { text: t }); setNoteFeedback(r.data); }
    catch { setNoteFeedback(null); }
    finally { setCheckingNote(false); }
  };

  const suggestEsito = async (text) => {
    const t = (text ?? act.notes ?? "").trim();
    if (t.length < 10) return;
    try {
      const r = await api.post("/activities/suggest-esito", { text: t });
      const d = r.data || {};
      if (d.accesso_dm && (d.dm_nome || d.dm_cognome || d.dm_ruolo)) {
        setAct((f) => ({
          ...f,
          decision_maker: true,
          dm_nome: (f.dm_nome || "").trim() ? f.dm_nome : (d.dm_nome || ""),
          dm_cognome: (f.dm_cognome || "").trim() ? f.dm_cognome : (d.dm_cognome || ""),
          dm_ruolo: (f.dm_ruolo || "").trim() ? f.dm_ruolo : (d.dm_ruolo || ""),
        }));
        setDmAi(true);
      }
    } catch { /* suggerimento best-effort */ }
  };

  const [act, setAct] = useState({ type: "telefonata", date: defaultDate || todayISO(), time: "", status: "svolto", notes: "", partner_id: partnerId, prospect_id: prospectId, decision_maker: false, dm_nome: "", dm_cognome: "", dm_ruolo: "" });
  const [pr, setPr] = useState(EMPTY_PR);
  const [prNonce, setPrNonce] = useState(0);

  // Reset del target ogni volta che si apre (utile quando pilotato dall'esterno con partner/prospect diversi)
  useEffect(() => {
    if (actOpen) {
      setAct({ type: "telefonata", date: defaultDate || todayISO(), time: "", status: "svolto", notes: "", partner_id: partnerId, prospect_id: prospectId, decision_maker: false, dm_nome: "", dm_cognome: "", dm_ruolo: "" });
      setNoteFeedback(null);
      setDmAi(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actOpen]);

  // Verifica automatica della nota (debounce mentre si scrive)
  useEffect(() => {
    if (!actOpen) return;
    const t = (act.notes || "").trim();
    if (t.length < 10) { setNoteFeedback(null); return; }
    const id = setTimeout(() => { checkNote(t); suggestEsito(t); }, 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act.notes, actOpen]);

  const submitAct = async () => {
    if (!act.date) { toast.error("Seleziona una data"); return; }
    const pid = partnerId || act.partner_id;
    if (!pid && !act.prospect_id) { toast.error("Seleziona un cliente o prospect"); return; }
    setBusy(true);
    try {
      const r = await api.post("/activities", {
        type: act.type, date: act.date, time: act.time || "",
        partner_id: pid || null, prospect_id: act.prospect_id || null, agency_id: agencyId || null,
        status: act.status, notes: act.notes || "", decision_maker: !!act.decision_maker,
        dm_nome: act.decision_maker ? (act.dm_nome || "") : "", dm_cognome: act.decision_maker ? (act.dm_cognome || "") : "", dm_ruolo: act.decision_maker ? (act.dm_ruolo || "") : "",
      });
      toast.success("Attività registrata");
      setActOpen(false);
      setNoteFeedback(null);
      setAct({ type: "telefonata", date: defaultDate || todayISO(), time: "", status: "svolto", notes: "", partner_id: partnerId, prospect_id: null, decision_maker: false, dm_nome: "", dm_cognome: "", dm_ruolo: "" });
      onChanged?.();
      maybeFunnelPrompt(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  const submitPr = async () => {
    const REQ = [["name", "Ragione sociale"], ["phone", "Telefono"], ["email", "Email"], ["region", "Regione"], ["province", "Provincia"], ["city", "Città"], ["address", "Indirizzo"], ["partita_iva", "Partita IVA"]];
    const missing = REQ.filter(([k]) => !String(pr[k] || "").trim()).map(([, l]) => l);
    if (missing.length) { toast.error("Compila i campi obbligatori: " + missing.join(", ")); return; }
    if (!String(pr.email || "").includes("@")) { toast.error("Email non valida"); return; }
    setBusy(true);
    try {
      await api.post("/prospects", {
        name: pr.name.trim(), region: pr.region || "", province: pr.province || "", city: pr.city || "",
        address: pr.address || "", cap: pr.cap || "", partita_iva: pr.partita_iva || "",
        referente: pr.referente || "", email: pr.email || "", phone: pr.phone || "",
        next_action: pr.next_action || "", notes: pr.notes || "", stage: "nuovo",
        categoria: pr.categoria || null,
        tipo_cliente: pr.categoria === "cliente_finale" ? pr.tipo_cliente : null,
        settore: pr.categoria === "cliente_finale" && pr.tipo_cliente === "azienda" ? pr.settore : null,
        fonte_contatto: pr.categoria === "cliente_finale" ? pr.fonte_contatto : null,
        note_inseritore: pr.categoria === "cliente_finale" ? pr.note_inseritore : null,
      });
      toast.success("Prospect creato");
      setPrOpen(false);
      setPr(EMPTY_PR);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  const setA = (k) => (e) => setAct((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const setP = (k) => (e) => setPr((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div className="flex flex-wrap items-center gap-2">
      {!hideTriggers && show.includes("activity") && (
        <Button variant={variant} size={size} onClick={() => { setNoteFeedback(null); setActOpen(true); }} data-testid="quickadd-activity-btn">
          <CalendarPlus className="mr-2 h-4 w-4" /> Nuova attività
        </Button>
      )}
      {!hideTriggers && show.includes("prospect") && (
        <Button variant={variant} size={size} onClick={() => { setPr(EMPTY_PR); setPrNonce((n) => n + 1); setPrOpen(true); }} data-testid="quickadd-prospect-btn">
          <UserPlus className="mr-2 h-4 w-4" /> Nuovo prospect
        </Button>
      )}

      <Dialog open={actOpen} onOpenChange={setActOpen}>
        <DialogContent className="sm:max-w-md" data-testid="quickadd-activity-dialog">
          <DialogHeader>
            <DialogTitle>Nuova attività</DialogTitle>
            <DialogDescription>{partnerName ? `Cliente: ${partnerName}` : prospectName ? `Prospect: ${prospectName}` : "Registra una telefonata, video call o contatto (svolta o pianificata). La visita si registra separatamente."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!partnerId && !prospectId && (
              <div className="space-y-1.5"><Label className="text-xs">Cliente o prospect</Label>
                <TargetCombobox value={act.prospect_id || act.partner_id} onSelect={(t) => setAct((f) => ({ ...f, partner_id: t.kind === "partner" ? t.id : null, prospect_id: t.kind === "prospect" ? t.id : null }))} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Tipo</Label>
                <Select value={act.type} onValueChange={setA("type")}>
                  <SelectTrigger data-testid="quickadd-act-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Stato</Label>
                <Select value={act.status} onValueChange={setA("status")}>
                  <SelectTrigger data-testid="quickadd-act-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="svolto">Svolta</SelectItem>
                    <SelectItem value="pianificato">Pianificata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Data</Label><DatePicker value={act.date} onChange={setA("date")} data-testid="quickadd-act-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Ora (opz.)</Label><Input type="time" value={act.time} onChange={setA("time")} data-testid="quickadd-act-time" /></div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Note</Label>
                <span className="flex items-center text-[10px] text-muted-foreground" data-testid="quickadd-note-ai-status">
                  {checkingNote ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Verifica AI…</> : <><Sparkles className="mr-1 h-3 w-3 text-primary" /> Verifica automatica AI</>}
                </span>
              </div>
              <Textarea rows={3} value={act.notes} onChange={setA("notes")} placeholder="Cosa è emerso, esito, prossimi passi…" data-testid="quickadd-act-notes" />
              {noteFeedback && (
                <div className={`rounded-md border p-2 text-[11px] ${noteFeedback.sufficiente ? "border-green-500/40 bg-green-500/10 text-green-400" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`} data-testid="quickadd-note-feedback">
                  {noteFeedback.sufficiente ? "✓ " : "✗ "}{noteFeedback.motivo}
                </div>
              )}
            </div>
            {act.type !== "visita_partner" && (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-black/20 p-2.5" data-testid="quickadd-dm-row">
                <Checkbox checked={act.decision_maker} onCheckedChange={(v) => setAct((f) => ({ ...f, decision_maker: !!v }))} data-testid="quickadd-dm-checkbox" />
                <span className="text-xs">Parlato con il <strong>decision maker</strong> <span className="text-muted-foreground">(fa avanzare il prospect allo stadio “Decision maker”)</span></span>
              </label>
            )}
            {act.type !== "visita_partner" && act.decision_maker && (
              <div className="grid grid-cols-1 gap-3 rounded-md border border-white/10 bg-black/20 p-2.5 sm:grid-cols-3" data-testid="quickadd-dm-fields">
                {dmAi && (
                  <div className="sm:col-span-3 flex items-center gap-1 text-[10px] text-primary" data-testid="quickadd-dm-ai-hint">
                    <Sparkles className="h-3 w-3" /> Proposto dall'AI dalla nota — verifica e correggi se serve
                  </div>
                )}
                <div className="space-y-1.5"><Label className="text-xs">Nome</Label><Input value={act.dm_nome} onChange={setA("dm_nome")} data-testid="quickadd-dm-nome" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Cognome</Label><Input value={act.dm_cognome} onChange={setA("dm_cognome")} data-testid="quickadd-dm-cognome" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Ruolo</Label><Input value={act.dm_ruolo} onChange={setA("dm_ruolo")} placeholder="es. Titolare, Resp. acquisti" data-testid="quickadd-dm-ruolo" /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActOpen(false)}>Annulla</Button>
            <Button onClick={submitAct} disabled={busy} data-testid="quickadd-act-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva attività"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={prOpen} onOpenChange={setPrOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="quickadd-prospect-dialog">
          <DialogHeader>
            <DialogTitle>Nuovo prospect</DialogTitle>
            <DialogDescription>Inserisci un nuovo potenziale cliente. Sarà visibile a te e alla Direzione.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2 text-[11px] text-muted-foreground">I campi con <span className="text-red-500">*</span> sono obbligatori.</div>
            <div className="space-y-1.5 sm:col-span-2 rounded-lg border-2 border-primary/40 p-3" data-testid="quickadd-cliente-finale-box">
              <label className="flex items-center gap-2 text-sm font-semibold">
                <Checkbox checked={pr.categoria === "cliente_finale"} onCheckedChange={(v) => setPr((f) => ({ ...f, categoria: v ? "cliente_finale" : "" }))} data-testid="quickadd-pf-cliente-finale-toggle" />
                Prospect cliente finale
                <span className="text-[11px] font-normal text-muted-foreground">(escluso dalla mappa, categoria dedicata)</span>
              </label>
              {pr.categoria === "cliente_finale" && (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label className="text-xs">Tipo</Label>
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant={pr.tipo_cliente === "azienda" ? "default" : "outline"} onClick={() => setPr((f) => ({ ...f, tipo_cliente: "azienda" }))} data-testid="quickadd-pf-tipo-azienda">Azienda</Button>
                      <Button type="button" size="sm" variant={pr.tipo_cliente === "privato" ? "default" : "outline"} onClick={() => setPr((f) => ({ ...f, tipo_cliente: "privato" }))} data-testid="quickadd-pf-tipo-privato">Privato</Button>
                    </div>
                  </div>
                  {pr.tipo_cliente === "azienda" && (
                    <FlagPicker key={`qa-set-${prNonce}`} options={SETTORI} label="Settore di attività" testid="quickadd-pf-settore" value={pr.settore} onChange={(v) => setPr((f) => ({ ...f, settore: v }))} />
                  )}
                  <FlagPicker key={`qa-fonte-${prNonce}`} options={FONTI} label="Da dove arriva il contatto" testid="quickadd-pf-fonte" value={pr.fonte_contatto} onChange={(v) => setPr((f) => ({ ...f, fonte_contatto: v }))} />
                  <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Note di chi inserisce</Label><Textarea rows={2} value={pr.note_inseritore} onChange={setP("note_inseritore")} data-testid="quickadd-pf-note-inseritore" /></div>
                </div>
              )}
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">{pr.categoria === "cliente_finale" && pr.tipo_cliente === "privato" ? "Nome e cognome *" : "Ragione sociale *"}</Label><Input value={pr.name} onChange={setP("name")} data-testid="quickadd-pr-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Regione <span className="text-red-500">*</span></Label>
              {regions.length > 0 ? (
                <Select value={pr.region || undefined} onValueChange={setP("region")}>
                  <SelectTrigger data-testid="quickadd-pr-region"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              ) : <Input value={pr.region} onChange={setP("region")} data-testid="quickadd-pr-region-input" />}
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Provincia <span className="text-red-500">*</span></Label><Input value={pr.province} onChange={setP("province")} data-testid="quickadd-pr-province" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Città <span className="text-red-500">*</span></Label><Input value={pr.city} onChange={setP("city")} data-testid="quickadd-pr-city" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Indirizzo <span className="text-red-500">*</span></Label><Input value={pr.address} onChange={setP("address")} data-testid="quickadd-pr-address" /></div>
            <div className="space-y-1.5"><Label className="text-xs">CAP</Label><Input value={pr.cap} onChange={setP("cap")} data-testid="quickadd-pr-cap" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Partita IVA <span className="text-red-500">*</span></Label><Input value={pr.partita_iva} onChange={setP("partita_iva")} data-testid="quickadd-pr-piva" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Referente</Label><Input value={pr.referente} onChange={setP("referente")} data-testid="quickadd-pr-referente" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Email <span className="text-red-500">*</span></Label><Input value={pr.email} onChange={setP("email")} data-testid="quickadd-pr-email" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Telefono <span className="text-red-500">*</span></Label><Input value={pr.phone} onChange={setP("phone")} data-testid="quickadd-pr-phone" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Prossima azione</Label><Input value={pr.next_action} onChange={setP("next_action")} data-testid="quickadd-pr-next" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Note</Label><Textarea rows={2} value={pr.notes} onChange={setP("notes")} data-testid="quickadd-pr-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrOpen(false)}>Annulla</Button>
            <Button onClick={submitPr} disabled={busy} data-testid="quickadd-pr-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea prospect"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
